import PageShell from "@/components/PageShell";
import {
  Building2, Scale, ShieldCheck, AlertTriangle, ClipboardCheck, Lock, UserCheck,
  Award, Split, Compass,
} from "lucide-react";
import { GOVERNANCE_PRINCIPLES } from "@/lib/content";
import { Link } from "react-router-dom";

const ICONS = {
  Building2, Scale, ShieldCheck, AlertTriangle, ClipboardCheck, Lock, UserCheck,
  Award, Split, Compass,
};

export default function Governance() {
  return (
    <PageShell
      testId="governance-page"
      title="Governance & Standards — Salihi Group"
      description="Salihi Group's governance framework covers corporate oversight, professional independence, ethical conduct, risk management, compliance, confidentiality, accountability, quality assurance, conflict management, and responsible decision-making."
      eyebrow="Governance"
      heading={<>Governance & <em className="text-gold not-italic">standards</em>.</>}
      subheading="How we hold ourselves accountable across the Group."
      breadcrumbs={[{ label: "Governance" }]}
    >
      {/* Group as parent + role */}
      <SectionHeader eyebrow="Group Governance" heading="The role of Salihi Group as the parent organisation." />
      <div className="grid lg:grid-cols-12 gap-12 mb-24">
        <div className="lg:col-span-7 space-y-6 text-navy text-[17px] leading-[1.75] font-light">
          <p>
            Salihi Group is the parent holding company overseeing seven specialised subsidiaries. The Group&apos;s role is
            to set the framework within which every subsidiary operates — from ethical conduct and confidentiality
            standards to engagement documentation, quality reviews, and reporting.
          </p>
          <p>
            Each subsidiary retains the professional independence its discipline requires — legal, consulting,
            engineering, investment, mining, or workforce services — while working within one shared identity and
            one shared standard of client experience.
          </p>
          <p>
            The Group's governance approach is centred on four commitments: <strong className="font-normal">confidentiality
            as a default</strong>, <strong className="font-normal">documented process</strong> and clear engagement terms,
            <strong className="font-normal"> senior oversight</strong> on every engagement, and
            <strong className="font-normal"> accountability</strong> for delivery quality across all seven subsidiaries.
          </p>
        </div>
        <aside className="lg:col-span-5">
          <div className="bg-navy text-white p-8 md:p-10">
            <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-4">Group Structure</div>
            <ul className="space-y-3 text-sm">
              <Row label="Legal form" value="Holding company" />
              <Row label="Subsidiaries" value="7 specialised companies" />
              <Row label="Head office" value="Mogadishu, Somalia" />
              <Row label="Established" value="2020" />
              <Row label="Governing law" value="Federal Republic of Somalia" />
            </ul>
          </div>
        </aside>
      </div>

      {/* Governance principles grid */}
      <SectionHeader eyebrow="Governance Principles" heading="Ten principles that shape decisions across the Group." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-24">
        {GOVERNANCE_PRINCIPLES.map((p, i) => {
          const Icon = ICONS[p.icon] || Building2;
          return (
            <article key={p.title} data-testid={`governance-principle-${i}`}
                     className="bg-white border border-softgray p-6 hover:border-gold transition-colors">
              <Icon size={22} strokeWidth={1.5} className="text-gold mb-4" />
              <div className="text-mutedink text-[10px] uppercase tracking-widest font-mono mb-2">0{(i + 1).toString().padStart(2, "0")}</div>
              <h3 className="font-serif text-navy text-lg mb-2 leading-tight">{p.title}</h3>
              <p className="text-mutedink text-[14px] leading-relaxed font-light">{p.body}</p>
            </article>
          );
        })}
      </div>

      {/* Ethics + Confidentiality deep-dive */}
      <div className="grid md:grid-cols-2 gap-8 mb-24">
        <article className="p-8 md:p-10 border border-softgray" data-testid="governance-ethics">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-3">Ethical Conduct</div>
          <h3 className="font-serif text-navy text-2xl mb-4">Applied consistently across every subsidiary.</h3>
          <p className="text-mutedink text-[15px] leading-relaxed font-light">
            Ethical standards are not delegated to individual subsidiaries. They are set at Group level — covering
            client engagement, conflicts of interest, communication, information handling, and outcomes reporting —
            and applied uniformly across legal, consulting, engineering, investment, mining, Oil & Gas, and workforce work.
          </p>
        </article>
        <article className="p-8 md:p-10 border border-softgray" data-testid="governance-confidentiality">
          <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-3">Confidentiality Framework</div>
          <h3 className="font-serif text-navy text-2xl mb-4">Enforced at systems, personnel, and process levels.</h3>
          <p className="text-mutedink text-[15px] leading-relaxed font-light">
            Access to client information is controlled and documented. Communication protocols are agreed with each
            client at the outset. Personnel are engaged under confidentiality undertakings. Cross-subsidiary
            information sharing is scoped, not automatic.
          </p>
        </article>
      </div>

      {/* Leadership placeholder */}
      <SectionHeader eyebrow="Leadership" heading="Names and biographies to follow." />
      <div className="max-w-3xl mb-24">
        <p className="text-mutedink text-[16px] leading-relaxed font-light">
          Salihi Group's leadership team is being formally introduced on this page. Details will be published here
          once the client has approved the biographies. In the interim, clients wanting to know who they would work
          with on a specific engagement are welcome to enquire directly.
        </p>
      </div>

      {/* CTA */}
      <div className="border-t-2 border-gold pt-14 text-center">
        <h3 className="font-serif text-navy text-3xl md:text-4xl font-light mb-4">
          Discuss a mandate with Salihi Group.
        </h3>
        <p className="text-mutedink max-w-2xl mx-auto mb-8">
          Whether the enquiry is legal, consulting, engineering, investment, mining, or workforce — the Group is set
          up to respond within a single, documented professional framework.
        </p>
        <Link to="/contact" className="btn-gold" data-testid="governance-cta-contact">Contact us</Link>
      </div>
    </PageShell>
  );
}

function SectionHeader({ eyebrow, heading }) {
  return (
    <div className="max-w-3xl mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="gold-hairline" />
        <span className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium">{eyebrow}</span>
      </div>
      <h2 className="font-serif text-navy text-3xl md:text-4xl font-light">{heading}</h2>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <li className="flex justify-between border-b border-white/10 pb-3">
      <span className="text-white/70">{label}</span>
      <span>{value}</span>
    </li>
  );
}
